import React, { useState } from 'react'
import { Controller } from 'react-hook-form'
import { RegisterOptions } from 'react-hook-form/dist/types/validator'
import { formText } from '../colors'
import { UseFormReturn } from 'react-hook-form/dist/types'
import { getError } from './FormTextField'
import styled from '@emotion/styled'
import Select, { ActionMeta, StylesConfig } from 'react-select'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { formatEnumString } from '../common'
import { theme } from '../mui'

export const enumStringToReactSelectOption = (value: string) => ({
  label: formatEnumString(value),
  value: value.toString(),
})

export const enumStringsToReactSelectOptions = (values: string[]) =>
  values.map((it) => enumStringToReactSelectOption(it))

export interface ReactSelectOption {
  label?: string
  value?: string
  group?: string
}

export const DropdownIndicator = () => (
  <ArrowDropDownIcon style={{ color: theme.palette.primary.main }} />
)

export const IndicatorSeparator = () => <></>

export const selectStyle: StylesConfig = {
  control: (provided) => ({
    ...provided,
    padding: '.5em',
  }),
  menuList: (provided) => ({
    ...provided,
    zIndex: '2',
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
}

interface FormSelectProps<T = ReactSelectOption> {
  useForm: UseFormReturn<any>
  isClearable?: boolean
  isSearchable?: boolean
  isMulti?: boolean
  label: string
  name: string
  options: T[]
  rules?: Omit<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >
  disabled?: boolean
  required?: boolean
  onChange?: (newValue: any, actionMeta: ActionMeta<any>) => void
  controlledValue?: T
}

interface LabelProps {
  floating: boolean
  error: boolean
}

interface WrapperProps {
  error: boolean
}

export const S = {
  Container: styled.div`
    position: relative;
  `,
  Label: styled.div<LabelProps>`
    left: 1rem;
    pointer-events: none;
    position: absolute;
    z-index: 1;
    color: ${(props) => (props.error ? theme.palette.error.main : formText)};
    top: ${(props) => (props.floating ? '-.5em' : '25%')};
    font-size: ${(props) => props.floating && '.75em'};
    padding: ${(props) => props.floating && '0 .5em'};
    background: ${(props) => props.floating && 'rgba(256, 256, 256, 1)'};

    transition: 0.2s ease all;
    -moz-transition: 0.2s ease all;
    -webkit-transition: 0.2s ease all;
  `,
  Wrapper: styled.div<WrapperProps>`
    & > div {
      & > div {
        border-color: ${(props) => props.error && theme.palette.error.main};
        & > div [id$='placeholder'] {
          color: ${(props) =>
      props.error && `${theme.palette.error.main} !important`};
        }
      }
    }
  `,
}

const FormSelect = <T = ReactSelectOption,>({
  useForm,
  isClearable,
  isSearchable,
  isMulti,
  label,
  name,
  options,
  rules,
  disabled,
  required,
  onChange,
  controlledValue,
}: FormSelectProps<T>) => {
  const [thisFocused, setFocus] = useState(false)
  const value = useForm.watch(name)
  const error = getError(useForm.formState.errors, name)

  const onFocus = () => {
    setFocus(true)
  }
  const onBlur = () => {
    setFocus(false)
  }
  const passValue =
    onChange == null ? {} : { onChange: onChange, value: controlledValue }

  return (
    <Controller
      name={name}
      control={useForm.control}
      rules={rules}
      render={({ field }) => (
        <S.Container>
          <S.Label
            floating={thisFocused || value != null || controlledValue != null}
            error={error != null}
          >
            {error == null ? label : `${label} is ${error.type}`}{' '}
            {required && <span>*</span>}
          </S.Label>
          <S.Wrapper error={error != null}>
            <Select
              {...field}
              isDisabled={disabled != null && disabled}
              placeholder=""
              options={options}
              onFocus={onFocus}
              onBlur={onBlur}
              isClearable={isClearable != null && isClearable}
              isSearchable={isSearchable}
              isMulti={isMulti}
              components={{
                DropdownIndicator,
                IndicatorSeparator,
              }}
              styles={selectStyle}
              menuPortalTarget={document.body}
              blurInputOnSelect={true}
              controlShouldRenderValue={!thisFocused}
              {...passValue}
            />
          </S.Wrapper>
        </S.Container>
      )}
    />
  )
}

export default FormSelect
